import React, { useState, useEffect } from 'react';
import './stock-stats.css';

const StockStats = () => {
    const [stats, setStats] = useState({
        totalProducts: 0,
        totalInventory: 0,
        totalValue: 0,
        tagStats: {}, // Store stats for each tag
        coreProducts: {} // Store core products as an object
    });

    useEffect(() => {
        const fetchStatsFromSessionStorage = () => {
            const inventoryData = JSON.parse(sessionStorage.getItem('inventoryData'));
            const coreProductsData = JSON.parse(sessionStorage.getItem('coreProductsData')) || {}; // Retrieve core products
            console.log('Retrieved inventory data:', inventoryData); // Log the data

            if (Array.isArray(inventoryData)) { // Check if it's an array
                const totalProducts = inventoryData.length; // Total number of products
                const totalInventory = inventoryData.reduce((sum, product) => {
                    return sum + (product.variants ? product.variants.reduce((variantSum, variant) => {
                        return variantSum + (variant.inventory_quantity || 0);
                    }, 0) : 0);
                }, 0);
                const totalValue = inventoryData.reduce((sum, product) => {
                    return sum + (product.variants ? product.variants.reduce((variantSum, variant) => {
                        return variantSum + (variant.inventory_quantity || 0) * (variant.price || 0);
                    }, 0) : 0);
                }, 0);

                // Initialize tag statistics
                const tagStats = {};

                // Collect unique tags and calculate stats
                inventoryData.forEach(product => {
                    if (Array.isArray(product.tags)) { // Check if tags is an array
                        product.tags.forEach(tag => {
                            if (!tagStats[tag]) {
                                tagStats[tag] = {
                                    totalProducts: 0,
                                    totalInventory: 0,
                                    totalValue: 0,
                                    coreProducts: {
                                        totalProducts: 0,
                                        totalInventory: 0,
                                        totalValue: 0
                                    }
                                };
                            }
                            tagStats[tag].totalProducts += 1; // Increment product count for the tag
                            tagStats[tag].totalInventory += product.variants ? product.variants.reduce((variantSum, variant) => {
                                return variantSum + (variant.inventory_quantity || 0);
                            }, 0) : 0; // Add inventory for the tag
                            tagStats[tag].totalValue += product.variants ? product.variants.reduce((variantSum, variant) => {
                                return variantSum + (variant.inventory_quantity || 0) * (variant.price || 0);
                            }, 0) : 0; // Add value for the tag
                        });
                    }
                });

                // Calculate core products statistics
                Object.values(coreProductsData).forEach(coreProduct => {
                    // Find the matching product in inventory data to get its tags
                    const matchingProduct = inventoryData.find(product => 
                        product.product_id.toString() === Object.keys(coreProductsData).find(key => 
                            coreProductsData[key].sku === coreProduct.sku
                        )
                    );

                    if (matchingProduct && Array.isArray(matchingProduct.tags)) {
                        matchingProduct.tags.forEach(tag => {
                            if (!tagStats[tag]) {
                                tagStats[tag] = {
                                    totalProducts: 0,
                                    totalInventory: 0,
                                    totalValue: 0,
                                    coreProducts: {
                                        totalProducts: 0,
                                        totalInventory: 0,
                                        totalValue: 0
                                    }
                                };
                            }
                            
                            // Increment core product count for the tag
                            tagStats[tag].coreProducts.totalProducts += 1;
                            
                            // Add inventory and value from the matching product's variants
                            if (matchingProduct.variants) {
                                const variantStats = matchingProduct.variants.reduce((stats, variant) => {
                                    return {
                                        inventory: stats.inventory + (variant.inventory_quantity || 0),
                                        value: stats.value + (variant.inventory_quantity || 0) * (variant.price || 0)
                                    };
                                }, { inventory: 0, value: 0 });

                                tagStats[tag].coreProducts.totalInventory += variantStats.inventory;
                                tagStats[tag].coreProducts.totalValue += variantStats.value;
                            }
                        });
                    }
                });

                setStats({
                    totalProducts,
                    totalInventory,
                    totalValue: totalValue.toFixed(2), // Format to 2 decimal places
                    tagStats, // Store tag statistics
                    coreProducts: coreProductsData // Store core products
                });
            } else {
                console.error('Inventory data is not in the expected format:', inventoryData);
                // Handle the case where inventoryData is not in the expected format
                setStats({
                    totalProducts: 0,
                    totalInventory: 0,
                    totalValue: 0,
                    tagStats: {},
                    coreProducts: {}
                });
            }
        };

        fetchStatsFromSessionStorage();
    }, []);

    // Utility function to format numbers with commas
    const formatNumber = (num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    return (
        <div className="stats-container">
            <h1 className="stats-title">Inventory Statistics</h1>
            <div className="overall-stats">
                <div className="stats-grid">
                    <div className="stat-card">
                        <h3>Total Products</h3>
                        <p className="stat-value">{formatNumber(stats.totalProducts)}</p>
                        <p className="stat-description">Total number of products</p>
                    </div>
                    <div className="stat-card">
                        <h3>Total Inventory</h3>
                        <p className="stat-value">{formatNumber(stats.totalInventory)}</p>
                        <p className="stat-description">Total items in stock</p>
                    </div>
                    <div className="stat-card">
                        <h3>Total Value</h3>
                        <p className="stat-value">{formatNumber(parseInt(stats.totalValue))} RON</p>
                        <p className="stat-description">Total value of inventory</p>
                    </div>
                </div>
            </div>
            
            <div className="tags-container">
                <h3>Category Statistics</h3>
                {Object.entries(stats.tagStats)
                    .filter(([tag]) => tag.startsWith("stock-"))
                    .map(([tag, tagStat], index) => (
                        <div key={index} className="tag-stats">
                            <h4>{tag.replace('stock-', '')}</h4>
                            <div className="stats-group">
                                <div className="stats-section">
                                    <h5>Current Inventory</h5>
                                    <div className="stats-grid">
                                        <div className="stat-card">
                                            <h3>Products</h3>
                                            <p className="stat-value">{formatNumber(tagStat.totalProducts)}</p>
                                        </div>
                                        <div className="stat-card">
                                            <h3>In Stock</h3>
                                            <p className="stat-value">{formatNumber(parseInt(tagStat.totalInventory))}</p>
                                        </div>
                                        <div className="stat-card">
                                            <h3>Value</h3>
                                            <p className="stat-value">{formatNumber(parseInt(tagStat.totalValue))} RON</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="stats-section">
                                    <h5>Core Products</h5>
                                    <div className="stats-grid">
                                        <div className="stat-card">
                                            <h3>Products</h3>
                                            <p className="stat-value">{formatNumber(tagStat.coreProducts.totalProducts)}</p>
                                        </div>
                                        <div className="stat-card">
                                            <h3>In Stock</h3>
                                            <p className="stat-value">{formatNumber(parseInt(tagStat.coreProducts.totalInventory))}</p>
                                        </div>
                                        <div className="stat-card">
                                            <h3>Value</h3>
                                            <p className="stat-value">{formatNumber(parseInt(tagStat.coreProducts.totalValue))} RON</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    );
};

export default StockStats;